// PWA Utilities - Conditional Registration Based on Route

// Define which routes should have PWA features
const PWA_ROUTES = {
  '/collect': {
    manifest: '/manifest-pos.json',
    name: 'POS Terminal'
  },
  '/pay': {
    manifest: '/manifest-payment.json',
    name: 'Payment Terminal'
  }
};

// Check if current route should be a PWA
export function isPWARoute() {
  const pathname = window.location.pathname;
  return Object.keys(PWA_ROUTES).some(route => pathname.startsWith(route));
}

// Get manifest for current route
export function getCurrentManifest() {
  const pathname = window.location.pathname;
  for (const [route, config] of Object.entries(PWA_ROUTES)) {
    if (pathname.startsWith(route)) {
      return config.manifest;
    }
  }
  return null;
}

// Dynamically add/remove manifest link
export function updateManifestLink() {
  // Remove any existing manifest link
  const existingLink = document.querySelector('link[rel="manifest"]');
  if (existingLink) {
    existingLink.remove();
  }

  // Add manifest only for PWA routes
  if (isPWARoute()) {
    const manifestPath = getCurrentManifest();
    if (manifestPath) {
      const link = document.createElement('link');
      link.rel = 'manifest';
      link.href = manifestPath;
      document.head.appendChild(link);
      console.log('[PWA] Manifest loaded:', manifestPath);
    }
  } else {
    console.log('[PWA] Non-PWA route, no manifest loaded');
  }
}

// Register service worker only for PWA routes
export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.log('[PWA] Service workers not supported');
    return;
  }

  // Only register if on a PWA route
  if (!isPWARoute()) {
    console.log('[PWA] Not a PWA route, skipping service worker registration');
    return;
  }

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        console.log('[PWA] Service worker registered:', registration.scope);

        // Check for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          console.log('[PWA] New service worker found, installing...');

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('[PWA] New service worker installed, ready to activate');
              // Optionally notify user about update
              if (window.confirm('New version available! Reload to update?')) {
                newWorker.postMessage({ type: 'SKIP_WAITING' });
                window.location.reload();
              }
            }
          });
        });
      })
      .catch((error) => {
        console.error('[PWA] Service worker registration failed:', error);
      });

    // Listen for controller change (new SW activated)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('[PWA] New service worker activated');
    });
  });
}

// Install prompt handling (for "Add to Home Screen")
let deferredPrompt = null;

export function setupInstallPrompt() {
  if (!isPWARoute()) {
    return null;
  }

  window.addEventListener('beforeinstallprompt', (e) => {
    console.log('[PWA] Install prompt available');
    // Prevent the mini-infobar from appearing
    e.preventDefault();
    // Store the event for later use
    deferredPrompt = e;
    
    // Dispatch custom event that components can listen to
    window.dispatchEvent(new CustomEvent('pwa-install-available'));
  });

  window.addEventListener('appinstalled', () => {
    console.log('[PWA] App installed successfully');
    deferredPrompt = null;
    window.dispatchEvent(new CustomEvent('pwa-installed'));
  });

  return deferredPrompt;
}

// Trigger install prompt (call this from a button click)
export async function showInstallPrompt() {
  if (!deferredPrompt) {
    console.log('[PWA] Install prompt not available');
    return false;
  }

  // Show the install prompt
  deferredPrompt.prompt();

  // Wait for user response
  const { outcome } = await deferredPrompt.userChoice;
  console.log('[PWA] Install prompt outcome:', outcome);

  // Clear the prompt
  deferredPrompt = null;

  return outcome === 'accepted';
}

// Check if app is running in standalone mode (installed as PWA)
export function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone || // iOS
         document.referrer.includes('android-app://');
}

// Get install status
export function getInstallStatus() {
  return {
    isInstallable: isPWARoute(),
    isInstalled: isStandalone(),
    canPrompt: deferredPrompt !== null
  };
}

// Unregister service worker (useful for debugging or cleanup)
export async function unregisterServiceWorker() {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      await registration.unregister();
      console.log('[PWA] Service worker unregistered');
    }
  }
}

// Clear all caches (useful for forcing fresh data)
export async function clearAllCaches() {
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map(name => caches.delete(name)));
    console.log('[PWA] All caches cleared');
  }
}

// Initialize PWA features
export function initializePWA() {
  console.log('[PWA] Initializing...');
  console.log('[PWA] Current route:', window.location.pathname);
  console.log('[PWA] Is PWA route:', isPWARoute());
  
  updateManifestLink();
  registerServiceWorker();
  setupInstallPrompt();
  
  console.log('[PWA] Initialization complete');
}